/**
 * Tour Exporter - Unified Viewer Bundle Export
 *
 * Generates a self-contained ZIP package for enterprise/admin users
 * to host tours on their own infrastructure.
 *
 * The exported package uses the SAME viewer code as xrgallery.com,
 * but initialized in export mode (no Firebase, embedded config).
 *
 * Package contents:
 * - index.html (full viewer with embedded config)
 * - config.json (backup of tour configuration)
 * - README.md (deployment instructions)
 */

import { Logger } from '../utils/Logger';
import JSZip from 'jszip';

const logger = Logger.getLogger('TourExporter');
import type { ExperienceConfig, TextureVariant } from '../types/config';
import type { Sequence } from '../api/SequenceAPI';

export interface ExportOptions {
  /** Include branding (XRGallery logo) */
  includeBranding?: boolean;
  /** Include navigation controls */
  includeControls?: boolean;
  /** Custom title for the HTML page */
  pageTitle?: string;
  /** Custom favicon URL (base64 or external URL) */
  faviconUrl?: string;
}

/**
 * Cache for the viewer bundle and CSS
 * These are fetched once and reused for multiple exports
 */
let cachedViewerBundle: string | null = null;
let cachedViewerCSS: string | null = null;
let cachedViewerHTML: string | null = null;

/**
 * Fetch and cache the pre-built viewer bundle
 * In development, this fetches from the built output
 * In production, it's embedded or fetched from CDN
 */
async function getViewerBundle(): Promise<string> {
  if (cachedViewerBundle) {
    return cachedViewerBundle;
  }

  try {
    // Try to fetch the pre-built export bundle
    const response = await fetch('/dist/viewer-bundle.iife.js');
    if (response.ok) {
      cachedViewerBundle = await response.text();
      return cachedViewerBundle;
    }
  } catch (error) {
    logger.warn('Could not fetch viewer bundle:', error);
  }

  // Fallback: Generate a minimal stub that indicates the bundle is not available
  // This shouldn't happen in production - the build process should create the bundle
  throw new Error(
    'Viewer bundle not found. Please run "npm run build:viewer" first to generate the export bundle.'
  );
}

/**
 * Extract CSS from viewer.html
 * This is done at build time ideally, but we can also do it at runtime
 */
async function getViewerCSS(): Promise<string> {
  if (cachedViewerCSS) {
    return cachedViewerCSS;
  }

  try {
    // Fetch viewer.html and extract the <style> content
    const response = await fetch('/viewer.html');
    if (response.ok) {
      const html = await response.text();

      // Extract everything between <style> and </style>
      const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
      if (styleMatch) {
        cachedViewerCSS = styleMatch[1];
        return cachedViewerCSS;
      }
    }
  } catch (error) {
    logger.warn('Could not fetch viewer CSS:', error);
  }

  // Fallback: Return minimal CSS
  return `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width: 100%; height: 100%; overflow: hidden; background: #000; }
    #renderCanvas { width: 100%; height: 100%; touch-action: none; display: block; }
  `;
}

/**
 * Get the HTML body content from viewer.html
 * Extracts all HTML elements but removes scripts (they'll be replaced with bundled viewer)
 */
async function getViewerHTMLBody(): Promise<string> {
  if (cachedViewerHTML) {
    return cachedViewerHTML;
  }

  // Fetch viewer.html - this is the single source of truth for UI
  const response = await fetch('/viewer.html');
  if (!response.ok) {
    throw new Error(
      `Failed to fetch viewer.html (${response.status}). Export requires access to the viewer template.`
    );
  }

  const html = await response.text();

  // Extract everything between <body> and </body>
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (!bodyMatch) {
    throw new Error('Could not parse viewer.html - no <body> tag found.');
  }

  let bodyContent = bodyMatch[1];

  // Remove all script tags (Firebase config, main.ts, inline scripts)
  // These will be replaced with our bundled viewer and embedded config
  bodyContent = bodyContent.replace(/<script[\s\S]*?<\/script>/gi, '');

  cachedViewerHTML = bodyContent.trim();
  return cachedViewerHTML;
}

/**
 * Generate the complete standalone HTML for export
 */
async function generateExportHTML(
  config: ExperienceConfig,
  options: ExportOptions = {}
): Promise<string> {
  const {
    includeBranding = false,
    pageTitle = config.experience?.title || 'VR Experience',
  } = options;

  // Get the bundled viewer code and CSS
  const viewerBundle = await getViewerBundle();
  const viewerCSS = await getViewerCSS();
  const viewerBody = await getViewerHTMLBody();

  // Generate the complete HTML
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
  <meta name="mobile-web-app-capable" content="yes">
  <meta name="apple-mobile-web-app-capable" content="yes">
  <title>${escapeHtml(pageTitle)}</title>
  <style>
${viewerCSS}
${!includeBranding ? `
    /* Hide branding in export mode */
    .powered-by { display: none !important; }
    #loadingDefaultBranding { display: none !important; }
` : ''}
  </style>
</head>
<body>
  ${viewerBody}

  <!-- Toolbar animation trigger (from viewer.html) -->
  <script>
    document.addEventListener('DOMContentLoaded', function() {
      requestAnimationFrame(function() {
        requestAnimationFrame(function() {
          document.querySelectorAll('.viewer-toolbar-animate').forEach(function(el) {
            el.classList.add('toolbar-ready');
          });
        });
      });
    });
  </script>

  <!-- Embedded tour configuration (export mode - no Firebase) -->
  <script>
    // This config is read by the viewer when Firebase credentials are not provided
    window.__XRGALLERY_CONFIG__ = ${JSON.stringify(config, null, 2)};
  </script>

  <!-- Bundled XRGallery Viewer (same code as hosted version) -->
  <script type="module">
${viewerBundle}
  </script>
</body>
</html>`;
}

/**
 * Generate README with deployment instructions
 */
function generateReadme(sequence: Sequence, options: ExportOptions = {}): string {
  const title = options.pageTitle || sequence.name || 'VR Tour';

  return `# ${title}

This is a self-contained VR tour package exported from XRGallery.

## What's Included

This export contains the **full XRGallery viewer** - the same code that powers
xrgallery.online. You get:

- ✅ Liquid glass UI effects
- ✅ Portal preview animations
- ✅ All hotspot types (info, audio, navigation)
- ✅ WebXR/VR support
- ✅ Video streaming (HLS)
- ✅ 3D model loading
- ✅ Responsive design

## Quick Start

1. Upload all files to your web server
2. Navigate to \`index.html\` in a browser
3. Click "Enter VR" to experience in virtual reality (requires VR headset)

## Files Included

- \`index.html\` - Complete standalone viewer with embedded config
- \`config.json\` - Tour configuration (backup/reference)
- \`README.md\` - This file

## Hosting Options

### Option 1: Static Hosting Services (Recommended)
Upload to any static hosting service:
- [Netlify](https://netlify.com) - Drag & drop your folder
- [Vercel](https://vercel.com) - Connect your repo
- [GitHub Pages](https://pages.github.com) - Free hosting
- [Cloudflare Pages](https://pages.cloudflare.com) - Fast global CDN

### Option 2: Your Own Server
Place files in your web server's public directory (nginx, Apache, IIS, etc.)

### Option 3: Local Testing
\`\`\`bash
# Python 3
python -m http.server 8000

# Node.js
npx serve .
\`\`\`

## HTTPS Requirement

**Important:** WebXR (VR mode) requires HTTPS in production.
- \`localhost\` works without HTTPS for testing
- Use a hosting service with automatic SSL (Netlify, Vercel)
- Or configure SSL on your own server

## Media URLs

All media (images, videos, 3D models) in this tour are loaded from external URLs.
Ensure these URLs remain accessible:

${extractMediaUrls(sequence.config).map(url => `- ${url}`).join('\n') || '- No external media URLs found'}

## Customization

### Modifying the Tour
Edit \`config.json\` and update the embedded config in \`index.html\`:

1. Edit \`config.json\` with your changes
2. Open \`index.html\` in a text editor
3. Find \`window.__XRGALLERY_CONFIG__\` and replace its value

### Updating Media URLs
If you need to change where media is hosted:
1. Upload your media to a new location
2. Update the URLs in both \`config.json\` and \`index.html\`

## Browser Support

- Chrome 90+ ✅
- Firefox 90+ ✅
- Safari 15+ ✅
- Edge 90+ ✅
- Quest Browser ✅ (for VR)
- Vision Pro Safari ✅

## What's Different from Hosted Version

This exported version:
- ❌ No analytics tracking
- ❌ No password protection
- ❌ No user accounts
- ✅ Same visual experience
- ✅ Same features and effects
- ✅ Works offline (after initial load)

## License

This tour was created with [XRGallery](https://xrgallery.online).
The viewer code is licensed for use with your XRGallery subscription.

---
Exported on ${new Date().toISOString().split('T')[0]}
`;
}

/**
 * Extract all media URLs from config for documentation
 */
function extractMediaUrls(config: ExperienceConfig): string[] {
  const urls: string[] = [];

  config.stages?.forEach(stage => {
    // Skybox URLs
    if (stage.skybox?.url) urls.push(stage.skybox.url);
    if (stage.skybox?.hlsUrl) urls.push(stage.skybox.hlsUrl);
    if (stage.skybox?.variants) {
      Object.values(stage.skybox.variants).forEach((variant: TextureVariant) => {
        if (variant?.url) urls.push(variant.url);
      });
    }

    // Model URLs
    stage.models?.forEach(model => {
      if (model.url) urls.push(model.url);
    });

    // Plane URLs
    stage.planes?.forEach(plane => {
      if (plane.url) urls.push(plane.url);
    });

    // Audio URLs
    if (stage.audioUrl) urls.push(stage.audioUrl);
    stage.hotspots?.forEach(hotspot => {
      if (hotspot.audioUrl) urls.push(hotspot.audioUrl);
    });
  });

  // Remove duplicates and sort
  return [...new Set(urls)].sort();
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Export a tour as a downloadable ZIP package
 */
export async function exportTourPackage(
  sequence: Sequence,
  options: ExportOptions = {}
): Promise<Blob> {
  const zip = new JSZip();

  // Get the config
  const config = sequence.config;

  // Set page title from sequence name if not provided
  const exportOptions: ExportOptions = {
    pageTitle: sequence.name,
    ...options
  };

  // Generate the complete HTML with bundled viewer
  logger.info('Generating export HTML...');
  const html = await generateExportHTML(config, exportOptions);
  zip.file('index.html', html);

  // Add config.json as backup/reference
  const configJson = JSON.stringify(config, null, 2);
  zip.file('config.json', configJson);

  // Add README
  const readme = generateReadme(sequence, exportOptions);
  zip.file('README.md', readme);

  // Generate the ZIP blob
  logger.info('Creating ZIP archive...');
  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 9 }
  });

  logger.info('Export complete, size:', (blob.size / 1024).toFixed(1), 'KB');
  return blob;
}

/**
 * Download a tour package (triggers browser download)
 */
export async function downloadTourPackage(
  sequence: Sequence,
  options: ExportOptions = {}
): Promise<void> {
  logger.info('Starting export for:', sequence.name);

  const blob = await exportTourPackage(sequence, options);
  logger.debug('Generated blob, size:', blob.size);

  // Create download link
  const url = URL.createObjectURL(blob);
  const filename = `${sanitizeFilename(sequence.name || 'tour')}.zip`;

  logger.debug('Triggering download:', filename);

  // Try using the modern File System Access API if available (Chrome 86+)
  if ('showSaveFilePicker' in window) {
    try {
      const handle = await window.showSaveFilePicker!({
        suggestedName: filename,
        types: [{
          description: 'ZIP Archive',
          accept: { 'application/zip': ['.zip'] }
        }]
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      logger.debug('Downloaded via File System Access API');
      URL.revokeObjectURL(url);
      return;
    } catch (err) {
      // User cancelled or API not available, fall back to anchor method
      const error = err as { name?: string };
      if (error.name !== 'AbortError') {
        logger.debug('File System Access API failed, falling back:', err);
      }
    }
  }

  // Fallback: Use anchor element with download attribute
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = url;
  link.download = filename;
  link.target = '_blank';

  document.body.appendChild(link);

  // Use requestAnimationFrame for better timing
  requestAnimationFrame(() => {
    link.click();
    logger.debug('Click triggered');

    // Cleanup after delay
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      logger.debug('Cleanup complete');
    }, 2000);
  });
}

/**
 * Sanitize filename for download
 */
function sanitizeFilename(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50) || 'tour';
}

/**
 * Clear cached assets (useful for development)
 */
export function clearExportCache(): void {
  cachedViewerBundle = null;
  cachedViewerCSS = null;
  cachedViewerHTML = null;
  logger.debug('Cache cleared');
}
